import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MonthNav from "@/components/MonthNav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Home as HomeIcon, ChevronRight, CheckCircle2, Activity, Pill, AlertTriangle, ClipboardCheck, Flag, FileText, AlertCircle, XCircle, Mail, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

function HealthScoreRing({ score, clientId }) {
  // 0..100 score → color band
  const clamped = Math.max(0, Math.min(100, score || 0));
  const band =
    clamped >= 85 ? { stroke: "#10b981", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Excellent" } :
    clamped >= 65 ? { stroke: "#2563eb", text: "text-primary", bg: "bg-blue-50", border: "border-blue-200", label: "Good" } :
    clamped >= 40 ? { stroke: "#f59e0b", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "At risk" } :
                    { stroke: "#dc2626", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Critical" };
  const circumference = 2 * Math.PI * 18;
  const dash = (clamped / 100) * circumference;
  return (
    <div
      className={`shrink-0 inline-flex items-center gap-2 px-2 py-1.5 rounded-md border ${band.border} ${band.bg}`}
      title={`Compliance Health Score: ${clamped}/100 (${band.label})`}
      data-testid={`health-score-${clientId}`}
    >
      <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="18" stroke="#E2E8F0" strokeWidth="4" fill="none" />
        <circle
          cx="22" cy="22" r="18"
          stroke={band.stroke}
          strokeWidth="4"
          fill="none"
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 22 22)"
        />
        <text x="22" y="26" textAnchor="middle"
              className={`font-mono font-bold ${band.text}`}
              style={{ fontSize: "12px", fill: band.stroke }}>
          {clamped}
        </text>
      </svg>
      <div className="leading-tight">
        <p className={`text-[9px] uppercase tracking-[0.18em] font-semibold ${band.text} flex items-center gap-1`}>
          <Activity className="w-3 h-3" /> Health
        </p>
        <p className={`text-[10px] font-semibold ${band.text}`} data-testid={`health-label-${clientId}`}>{band.label}</p>
      </div>
    </div>
  );
}

export default function AdminOversight() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [drillStatus, setDrillStatus] = useState({ by_client: {} });
  const [emarStats, setEmarStats] = useState(null);
  const [flagged, setFlagged] = useState(null);

  useEffect(() => {
    api.get(`/oversight-summary?year=${year}&month=${month}`).then(({ data }) => setData(data));
    api.get(`/evacuation-drills/status?year=${year}`).then(({ data }) => setDrillStatus(data));
  }, [year, month]);

  useEffect(() => {
    api.get(`/admin/emar-quick-stats`).then(({ data }) => setEmarStats(data)).catch(() => {});
    api.get(`/admin/flagged-reviews?days=7`).then(({ data }) => setFlagged(data)).catch(() => {});
  }, []);

  const [showDeprioritized, setShowDeprioritized] = useState(false);

  // Backend now returns rows sorted: real-active → sandbox/test → archived,
  // each alphabetical inside their bucket. Mirror that classification here
  // so we can let admins hide / reveal the deprioritized buckets.
  const rawClients = data?.clients || [];
  const STUB_RE = /^Client [A-Z0-9_]{4,}$/;
  const TEST_NAME = (nm) =>
    !!nm && (
      STUB_RE.test(nm) ||
      ["TEST_", "TEST ", "DEMO", "SAMPLE", "MedReq", "James Carter", "Maria Lopez", "Daniel Brown"]
        .some((p) => nm.includes(p))
    );
  const classify = (c) => c.is_archived ? "archived" : (TEST_NAME(c.client_name) ? "test" : "active");

  const activeClients = rawClients.filter((c) => classify(c) === "active");
  const deprioritizedClients = rawClients.filter((c) => classify(c) !== "active");
  const clients = showDeprioritized ? [...activeClients, ...deprioritizedClients] : activeClients;
  const hiddenCount = deprioritizedClients.length;
  const currentQuarter = Math.floor((month - 1) / 3) + 1;

  return (
    <div className="space-y-7" data-testid="admin-oversight">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-label">Master Dashboard</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Live Oversight</h1>
          <p className="text-sm text-muted-foreground">
            Per-client monthly completion across every contractor.
          </p>
        </div>
        <MissingDocsButton />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonthNav
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          testId="oversight-month-nav"
        />
        {hiddenCount > 0 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-deprioritized-toggle">
            <input
              type="checkbox"
              checked={showDeprioritized}
              onChange={(e) => setShowDeprioritized(e.target.checked)}
              className="cursor-pointer"
            />
            Show {hiddenCount} archived / sandbox profile{hiddenCount === 1 ? "" : "s"}
          </label>
        )}
      </div>

      {/* v23 — Quick eMAR Stats card (today's adherence, PRN follow-ups, active meds) */}
      {emarStats && (
        <Card className="p-5" data-testid="emar-quick-stats">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-primary" />
              <div>
                <p className="text-label">Quick eMAR Stats</p>
                <p className="text-xs text-muted-foreground">Agency-wide, live · {emarStats.date}</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/budget")}
              className="text-xs font-semibold text-primary hover:underline"
              data-testid="emar-stats-view-alerts"
            >
              View Client Budget Tracking →
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* 1. Today's adherence */}
            <div className="rounded-md border-2 border-emerald-200 bg-emerald-50/60 p-4" data-testid="stat-adherence">
              <div className="flex items-center gap-1.5 mb-1.5">
                <ClipboardCheck className="w-3.5 h-3.5 text-emerald-700" />
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-800">Today&apos;s adherence</p>
              </div>
              {emarStats.adherence_today_pct === null || emarStats.total_slots_today === 0 ? (
                <>
                  <p className="font-display font-bold text-3xl text-muted-foreground" data-testid="stat-adherence-pct">—</p>
                  <p className="text-[11px] text-muted-foreground mt-1">No eMAR slots logged yet today</p>
                </>
              ) : (
                <>
                  <p className={`font-display font-bold text-3xl ${
                    emarStats.adherence_today_pct >= 95 ? "text-emerald-700" :
                    emarStats.adherence_today_pct >= 80 ? "text-amber-700" : "text-red-700"
                  }`} data-testid="stat-adherence-pct">
                    {emarStats.adherence_today_pct}%
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {emarStats.administered_today} given · {emarStats.missed_today} missed
                    {" "}({emarStats.total_slots_today} slots)
                  </p>
                </>
              )}
            </div>

            {/* 2. PRN follow-up gaps */}
            <div className={`rounded-md border-2 p-4 ${
              emarStats.prn_missing_followup_24h > 0
                ? "border-amber-300 bg-amber-50/70"
                : "border-border bg-secondary/30"
            }`} data-testid="stat-prn-followup">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle className={`w-3.5 h-3.5 ${emarStats.prn_missing_followup_24h > 0 ? "text-amber-700" : "text-muted-foreground"}`} />
                <p className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${emarStats.prn_missing_followup_24h > 0 ? "text-amber-800" : "text-muted-foreground"}`}>
                  PRN follow-up gaps (24h)
                </p>
              </div>
              <p className={`font-display font-bold text-3xl ${
                emarStats.prn_missing_followup_24h > 0 ? "text-amber-700" : "text-foreground"
              }`} data-testid="stat-prn-missing">
                {emarStats.prn_missing_followup_24h}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {emarStats.prn_total_24h === 0
                  ? "No PRN administered in the last 24h"
                  : `${emarStats.prn_total_24h - emarStats.prn_missing_followup_24h} of ${emarStats.prn_total_24h} have a 30-min check`}
              </p>
            </div>

            {/* 3. Active meds (denominator) */}
            <div className="rounded-md border-2 border-border bg-secondary/30 p-4" data-testid="stat-active-meds">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Pill className="w-3.5 h-3.5 text-primary" />
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Active medications</p>
              </div>
              <p className="font-display font-bold text-3xl" data-testid="stat-active-meds-count">
                {emarStats.active_meds_count}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Routine + PRN, agency-wide
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* v26 — Flagged-for-review feed (last 7 days) */}
      {flagged && flagged.note_count > 0 && (
        <Card className="p-5 border-l-4 border-l-amber-500 bg-amber-50/40" data-testid="flagged-reviews-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-amber-700" />
              <div>
                <p className="text-label text-amber-900">Items contractors flagged for admin review</p>
                <p className="text-xs text-amber-800">
                  {flagged.flag_count} flag{flagged.flag_count === 1 ? "" : "s"} across {flagged.note_count} note{flagged.note_count === 1 ? "" : "s"} · since {flagged.since}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-mono font-bold text-sm" data-testid="flagged-count-badge">
              <Flag className="w-3.5 h-3.5" /> {flagged.flag_count}
            </span>
          </div>
          <div className="space-y-2 max-h-72 overflow-auto" data-testid="flagged-reviews-list">
            {(flagged.notes || []).map((n) => (
              <button
                key={n.id}
                onClick={() => navigate(`/admin/notes/${n.client_id}?y=${year}&m=${month}`)}
                className="w-full text-left rounded-md border border-amber-200 bg-white p-3 hover:bg-amber-50 transition-colors"
                data-testid={`flagged-note-${n.id}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                  <p className="font-semibold text-sm">
                    {n.client_name || "Client"} · <span className="text-muted-foreground font-normal">{n.contractor_name || "Contractor"}</span>
                  </p>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded">{n.date}</span>
                </div>
                <div className="space-y-2 mt-2">
                  {(n.review_flags || []).map((f, i) => (
                    <div key={i} className="text-xs text-amber-950 bg-amber-50/80 p-2.5 rounded border border-amber-200/80">
                      <div className="flex items-start gap-1.5 font-medium">
                        <Flag className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                        <span>
                          <span className="font-bold capitalize text-amber-900">{f.kind || "item"}</span>
                          {f.title ? ` — ${f.title}` : ""}
                        </span>
                      </div>
                      {f.note && (
                        <div className="mt-1 pl-5 text-amber-900/90">
                          <span className="font-semibold text-[11px] uppercase tracking-wider text-amber-800">Reason:</span> {f.note}
                        </div>
                      )}
                      {f.suggested_replacement && (
                        <div className="mt-1 pl-5 text-emerald-900 bg-emerald-50/80 p-1.5 rounded border border-emerald-200/60">
                          <span className="font-semibold text-[11px] uppercase tracking-wider text-emerald-800">Suggested Replacement:</span> {f.suggested_replacement}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="oversight-cards">
        {clients.length === 0 && (
          <Card className="p-6 col-span-full text-center text-sm text-muted-foreground">
            No clients found.
          </Card>
        )}
        {clients.map((c) => {
          const pct = data.available_days > 0 ? Math.round((c.completed_days / data.available_days) * 100) : 0;
          const isComplete = c.completed_days >= data.available_days && data.available_days > 0;
          const isArchived = !!c.is_archived;
          const tone = isArchived
            ? "bg-slate-100 text-slate-500 border-slate-300"
            : isComplete
            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
            : pct >= 75 ? "bg-blue-50 text-primary border-blue-300"
            : pct >= 40 ? "bg-amber-50 text-amber-700 border-amber-300"
            : "bg-red-50 text-red-700 border-red-300";
          return (
            <button
              key={c.client_id}
              onClick={() => navigate(`/admin/notes/${c.client_id}?y=${year}&m=${month}`)}
              className={`text-left clinical-card p-5 hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${isArchived ? "opacity-60" : ""}`}
              data-testid={`oversight-card-${c.client_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="font-display font-bold text-lg leading-tight truncate flex items-center gap-2">
                    {c.client_name}
                    {isArchived && (
                      <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-semibold" data-testid={`archived-badge-${c.client_id}`}>
                        Archived
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                    <HomeIcon className="w-3.5 h-3.5" /> {c.contractor_name || c.host_home_name}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <HealthScoreRing score={c.health_score ?? 0} clientId={c.client_id} />
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>

              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${tone}`} data-testid={`completion-pill-${c.client_id}`}>
                {isComplete && <CheckCircle2 className="w-3.5 h-3.5" />}
                <span className="font-mono font-semibold text-sm">
                  {c.completed_days} / {c.available_days}
                </span>
                <span className="text-xs">days complete</span>
              </div>

              <div
                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700"
                data-testid={`billable-pill-${c.client_id}`}
              >
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Billable Days This Month:</span>
                <span className="font-mono font-bold text-sm" data-testid={`billable-days-${c.client_id}`}>
                  {c.billable_days ?? 0}
                </span>
              </div>

              {/* v29 — Document Hub Completeness Badge (BSP excluded; 3 cores required) */}
              {(() => {
                const ds = c.doc_hub_status || {};
                const tone = ds.tone || "red";
                const cls =
                  tone === "green" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                  tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-700" :
                                      "bg-red-50 border-red-200 text-red-700";
                const Icon = tone === "green" ? CheckCircle2 : tone === "amber" ? AlertCircle : XCircle;
                const label = tone === "green"
                  ? "All core docs linked"
                  : tone === "amber"
                  ? `${ds.core_present}/${ds.core_total} core docs linked`
                  : "Core docs missing";
                return (
                  <div
                    className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${cls}`}
                    data-testid={`doc-hub-badge-${c.client_id}`}
                    title={`PCSP, Support Strategy, Room & Board. BSP is optional${ds.has_bsp ? " · BSP linked" : ""}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <FileText className="w-3 h-3" />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Docs:</span>
                    <span className="text-xs font-semibold" data-testid={`doc-hub-label-${c.client_id}`}>{label}</span>
                    {ds.has_bsp && (
                      <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold" data-testid={`doc-hub-bsp-${c.client_id}`}>BSP</span>
                    )}
                  </div>
                );
              })()}

              <div className="mt-3 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2">
                {c.has_medications ? "Daily Note + eMAR required" : "Daily Note required"}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function MissingDocsButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/missing-docs-report");
      setReport(data);
      setOpen(true);
    } catch (e) {
      toast.error("Could not load missing-docs report");
    } finally { setLoading(false); }
  };

  const copyReport = async () => {
    if (!report?.text_report) return;
    try {
      await navigator.clipboard.writeText(report.text_report);
      toast.success("Report copied to clipboard — paste into your email client");
    } catch (e) {
      toast.error("Copy failed — select & Ctrl+C from the text below instead");
    }
  };

  const mailtoUrl = report
    ? `mailto:?subject=${encodeURIComponent("OPPO Host Home Portal — Missing Document Report")}&body=${encodeURIComponent(report.text_report)}`
    : "#";

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={fetchReport}
        disabled={loading}
        data-testid="missing-docs-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
        {loading ? "Loading…" : "Missing-docs report"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Missing Document Report</DialogTitle>
            <DialogDescription>
              Live snapshot of every active client whose core docs (PCSP, Support Strategy, Room &amp; Board) aren&apos;t fully linked.
              BSP is intentionally excluded — it&apos;s optional.
            </DialogDescription>
          </DialogHeader>

          {report && (
            <div className="space-y-3" data-testid="missing-docs-modal">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border-2 border-emerald-200 bg-emerald-50 p-2" data-testid="report-complete">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800">Complete</p>
                  <p className="font-display font-bold text-2xl text-emerald-700">{report.complete_count}</p>
                </div>
                <div className="rounded-md border-2 border-amber-200 bg-amber-50 p-2" data-testid="report-amber">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">Amber</p>
                  <p className="font-display font-bold text-2xl text-amber-700">{report.amber_count}</p>
                </div>
                <div className="rounded-md border-2 border-red-200 bg-red-50 p-2" data-testid="report-red">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-red-800">Red</p>
                  <p className="font-display font-bold text-2xl text-red-700">{report.red_count}</p>
                </div>
              </div>

              <pre
                className="text-[11px] font-mono whitespace-pre-wrap bg-secondary/40 border border-border rounded-md p-3 max-h-[40vh] overflow-auto"
                data-testid="report-text"
              >{report.text_report}</pre>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={copyReport} data-testid="copy-report">
              <Copy className="w-4 h-4 mr-1.5" /> Copy to clipboard
            </Button>
            <Button asChild data-testid="email-report">
              <a href={mailtoUrl}>
                <Mail className="w-4 h-4 mr-1.5" /> Email this to me
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
